import i18n from "../../i18n";
import { normalizeImportReport } from "../../components/ImportReport/importReport";
import { KINDS, buildCardCopy, combinedSimulationHint } from "./labels";
import { humanizeImportError } from "./humanizeError";
import { deleteItemLabel, translateDeleteError } from "./deleteErrorCodes";
// The reducers write these exact status values into job.projects[] — the
// enum lives with the reducer that owns the write side.
import { PROJECT_STATUS } from "../import-services/importReducer";

// Capacity-reset finalize sub-steps (backend WsProgressDTO.phase). WRITING_ROWS keeps
// the row-count label below, which needs the count the frame carries.
const RUN_PHASE_KEYS = {
  PREPARING: { key: "progressNotifications.kinds.generativeScheduling.phases.preparing", fallback: "Preparing the schedule…" },
  SIMULATING: { key: "progressNotifications.kinds.generativeScheduling.phases.simulating", fallback: "Computing the schedule…" },
  APPLYING: { key: "progressNotifications.kinds.generativeScheduling.phases.applying", fallback: "Applying the schedule…" },
};

const FINALIZING_PHASE_KEYS = {
  LOADING_EXISTING_ROWS: {
    key: "progressNotifications.resetCapacity.phases.loadingExistingRows",
    fallback: "Loading existing capacity rows…",
  },
  RESEEDING_STOCK: {
    key: "progressNotifications.resetCapacity.phases.reseedingStock",
    fallback: "Restoring the stock at the date of review…",
  },
};

/**
 * Card shape emitted by the selector and consumed by ProgressNotificationStack.
 *
 * @typedef {Object} ProgressCard
 * @property {string} id            stable per job instance: `${kind}:${jobId ?? startedAt}`
 * @property {string} jobId         the UUID from the 202 response (null for legacy/compat)
 * @property {string} kind          one of KINDS[].kind
 * @property {"running"|"done"|"warning"|"error"} state
 * @property {string} label
 * @property {string} target
 * @property {number} total
 * @property {number} done          completed + failed (drives running percent/ETA)
 * @property {number} completedCount
 * @property {number} failedCount
 * @property {number} remaining
 * @property {number} completedPercent  terminal split-bar green share, 0..100
 * @property {number} failedPercent     terminal split-bar red/amber share, 0..100
 * @property {string|null} currentItemLabel  running only: the in-flight item
 * @property {string|null} combinedRunHint  running only, portfolioSimulation with >1 project: clarifies the projects share one combined run
 * @property {Array<{label: string, message: string|null, raw: string|null}>} failedItems
 * @property {number|null} etaSeconds
 * @property {string|null} error    humanised top-level error message
 * @property {number} percent       0..100
 * @property {number} startedAt
 */

// "warning" = the job FINISHED and parts of it worked: re-running the whole
// thing would be wrong, unlike "error" where nothing succeeded. A top-level
// error always wins — it means the job itself aborted (interrupted, fatal),
// so partial counts describe an unfinished run, not a partial success.
function deriveState(job) {
  if (job.active) return "running";
  if (job.error) return "error";
  const failed = job.failedCount || 0;
  const completed = job.completedCount || 0;
  if (failed > 0 && completed > 0) return "warning";
  if (failed > 0) return "error";
  return "done";
}

const PROJECT_FAILED = PROJECT_STATUS.FAILED;
const PROJECT_IN_PROGRESS = PROJECT_STATUS.IN_PROGRESS;

// The in-flight item to surface as "Now: …" while running. Prefer an
// IN_PROGRESS entry; when a backend only sends COMPLETED/FAILED frames,
// fall back to the most recently finished label.
function deriveCurrentItemLabel(projects) {
  let inProgress = null;
  let latestFinished = null;
  for (const p of projects) {
    if (p.status === PROJECT_IN_PROGRESS) {
      if (!inProgress || (p.startedAt || 0) >= (inProgress.startedAt || 0)) {
        inProgress = p;
      }
    } else if (p.completedAt != null) {
      if (!latestFinished || (p.completedAt || 0) >= (latestFinished.completedAt || 0)) {
        latestFinished = p;
      }
    }
  }
  return itemLabel(inProgress) ?? itemLabel(latestFinished) ?? null;
}

function itemLabel(item) {
  if (!item) return undefined;
  return deleteItemLabel(item.kind, item.name) || item.label;
}

// Outcome split for the terminal bar. The two fills are computed
// cumulatively so the rounded shares can never sum past 100 and clip inside
// the flex track, and the denominator falls back to the processed count when
// the total never arrived (dropped QUEUED frame).
//
// "done" is always a full green bar (the state asserts total success; stale
// counts must not contradict it). An "error" job ABORTED, so whatever it
// didn't complete is part of the failure — the red share is the remainder,
// not just the items that reported a FAILED frame (an abort before any item
// frame would otherwise paint an empty bar).
function deriveOutcomePercents(state, completedCount, done, total) {
  if (state === "done") return { completedPercent: 100, failedPercent: 0 };
  if (state === "running") return { completedPercent: 0, failedPercent: 0 };
  const denominator = total > 0 ? total : done;
  if (denominator <= 0) {
    return { completedPercent: 0, failedPercent: state === "error" ? 100 : 0 };
  }
  const completedPercent = Math.min(100, Math.round((completedCount / denominator) * 100));
  if (state === "error") {
    return { completedPercent, failedPercent: 100 - completedPercent };
  }
  const failedPercent =
    Math.min(100, Math.round((done / denominator) * 100)) - completedPercent;
  return { completedPercent, failedPercent };
}

// A FAILED frame can land before the label one (the reducers upsert by index),
// leaving a nameless item. Name it by position rather than rendering a blank row.
function itemFallbackLabel(index) {
  return index == null
    ? i18n.t("progressNotifications.unnamedItem", "Unnamed item")
    : i18n.t("progressNotifications.itemN", {
        n: index + 1,
        defaultValue: `Item ${index + 1}`,
      });
}

// jobKey is the key the reducer files this job under — it IS what a dismiss
// must target. It only differs from job.jobId on entries written before the
// 202 body landed; falling back to it keeps a dismiss precise instead of
// degrading to the slice-wide "all".
function mapJobToCard(kind, job, jobKey = null) {
  if (!job || (!job.open && !job.active && !job.terminatedAt)) return null;
  const state = deriveState(job);
  const total = job.totalProjects || 0;
  let completedCount = job.completedCount || 0;
  const failedCount = job.failedCount || 0;
  // "done" asserts every item succeeded (failedCount is 0 by definition) —
  // counts can lag behind reality (rehydration snapshot, dropped trailing
  // frames), so the display must not contradict the state.
  if (state === "done" && total > 0) completedCount = total;
  const done = completedCount + failedCount;
  const startedAt = job.startedAt || 0;
  const { label, target: baseTarget } = buildCardCopy(state, kind, job);
  // A terminal frame may carry a concrete outcome — proof the job changed
  // data, not just that it ran. Prefer the numeric form (localized here);
  // fall back to the server-composed label.
  const target = !job.active
    ? job.resultRows != null
      ? i18n.t("progressNotifications.resetCapacity.rowsWritten", {
          n: job.resultRows.toLocaleString(i18n.language),
          defaultValue: `${job.resultRows.toLocaleString(i18n.language)} capacity rows written`,
        })
      : job.resultSummary || baseTarget
    : baseTarget;
  const isTerminalFailure = state === "error" || state === "warning";
  // Only errors humanize a top-level message (with the generic fallback
  // for a null error string, as before). Warnings never carry job.error —
  // deriveState routes any aborted job to "error" — and fabricating the
  // fallback would put a total-failure message on a partial success.
  // A backend reason code wins over parsing the English error text; the raw
  // text stays available under "Technical details".
  const coded = state === "error" ? translateDeleteError(job.errorCode, job.errorParams) : null;
  const humanized =
    state === "error" && !coded ? humanizeImportError(job.error || null, kind) : null;
  const error = coded?.message ?? humanized?.message ?? null;
  const actionHint = coded?.actionHint ?? humanized?.actionHint ?? null;
  const rawError = coded ? job.error ?? null : humanized?.raw ?? null;
  const percent =
    total > 0
      ? Math.min(100, Math.round((done / total) * 100))
      : state === "running"
        ? 5
        : 100;
  // Not every slice stores per-item frames (generativeScheduling has no
  // projects[]) — those still reach warning via counts, just without a list.
  const projects = job.projects || [];
  // A finalizing job (post-items phase, e.g. capacity reset's atomic swap)
  // surfaces its own label instead of the last finished item — the bar is
  // full but the job is still writing.
  const currentItemLabel =
    state === "running"
      ? job.phase && RUN_PHASE_KEYS[job.phase]
        ? i18n.t(RUN_PHASE_KEYS[job.phase].key, { defaultValue: RUN_PHASE_KEYS[job.phase].fallback })
        : job.finalizing
        ? job.finalizingPhase && FINALIZING_PHASE_KEYS[job.finalizingPhase]
          ? i18n.t(FINALIZING_PHASE_KEYS[job.finalizingPhase].key, {
              defaultValue: FINALIZING_PHASE_KEYS[job.finalizingPhase].fallback,
            })
          : job.finalizingRows != null
          ? i18n.t("progressNotifications.resetCapacity.finalizingRows", {
              n: job.finalizingRows.toLocaleString(i18n.language),
              defaultValue: `Writing ${job.finalizingRows.toLocaleString(i18n.language)} capacity rows…`,
            })
          : job.finalizingScopes != null
          ? i18n.t("progressNotifications.scopeCsvImport.finalizingScopes", {
              n: job.finalizingScopes.toLocaleString(i18n.language),
              defaultValue: `Creating dependencies for ${job.finalizingScopes.toLocaleString(i18n.language)} scopes…`,
            })
          : job.finalizingLabel ||
            i18n.t("progressNotifications.finalizing", {
              defaultValue: "Finalizing…",
            })
        : deriveCurrentItemLabel(projects)
      : null;
  // Per-item reasons: humanize only a real per-item error. Passing null
  // would fabricate the generic JOB-level failure sentence ("We couldn't
  // import your file") and glue it to an item's label as if it were its
  // reason — the same fabrication the job-level path avoids.
  const failedItems = isTerminalFailure
    ? projects
        .filter((p) => p.status === PROJECT_FAILED)
        .map((p) => ({
          label: itemLabel(p) || itemFallbackLabel(p.index),
          message:
            translateDeleteError(p.errorCode, p.errorParams)?.message ??
            (p.error ? humanizeImportError(p.error, kind)?.message ?? null : null),
          raw: p.error ?? null,
        }))
    : [];
  const jobId = job.jobId ?? jobKey ?? null;
  // Library CSV import: the final CsvValidationReportDTO (per-object
  // counters, warnings, row-level errors). Warnings never fail the import —
  // the card stays "done" and surfaces them as a separate chip + detail.
  const importReport = normalizeImportReport(job.report) ? job.report : null;
  const warningCount = importReport ? normalizeImportReport(importReport).warnings.length : 0;
  return {
    id: `${kind}:${jobId ?? startedAt}`,
    jobId,
    kind,
    state,
    label,
    target,
    total,
    done,
    completedCount,
    failedCount,
    remaining: Math.max(0, total - done),
    ...deriveOutcomePercents(state, completedCount, done, total),
    currentItemLabel,
    // Every item is counted but the job is still writing: the bar must not read as a
    // finished 100 %.
    finalizing: state === "running" && Boolean(job.finalizing),
    combinedRunHint: combinedSimulationHint(kind, state, total),
    failedItems,
    etaSeconds: job.etaSeconds ?? null,
    error,
    actionHint,
    rawError,
    importReport,
    warningCount,
    percent,
    startedAt,
  };
}

/**
 * Returns an array of cards for a given kind's slice.
 * The slice is now { jobs: { [jobId]: JobState } }.
 * Falls back gracefully to the old flat shape for backward compat during migration.
 */
function mapSliceToCards(kind, sliceState) {
  if (!sliceState) return [];

  // New multi-job shape: { jobs: {} }
  if (sliceState.jobs) {
    return Object.entries(sliceState.jobs)
      .map(([jobKey, job]) => mapJobToCard(kind, job, jobKey))
      .filter(Boolean)
      .sort((a, b) => (b.startedAt || 0) - (a.startedAt || 0));
  }

  // Legacy flat shape (backward compat): wrap in a single-card array
  const card = mapJobToCard(kind, sliceState);
  return card ? [card] : [];
}

// Per-slice card cache keyed by slice reference (immer gives a new reference
// on any change). Without it, one running job's WS frames would recompute
// EVERY slice's cards — including re-humanizing the per-item errors of a
// lingering warning card, which scales with its failed-item count.
const sliceCardsCache = new WeakMap();

function cardsForSlice(kind, sliceState) {
  if (!sliceState || typeof sliceState !== "object") return [];
  const cached = sliceCardsCache.get(sliceState);
  if (cached) return cached;
  const cards = mapSliceToCards(kind, sliceState);
  sliceCardsCache.set(sliceState, cards);
  return cards;
}

/**
 * Tiny memo keyed by slice identity tuple. Recomputes only when a slice changes.
 */
let lastInputs = null;
let lastCards = [];
let lastAggregate = { running: 0, done: 0, warnings: 0, errors: 0, total: 0, percent: 0 };

function compute(state) {
  const inputs = KINDS.map(({ stateKey }) => state[stateKey]);
  if (lastInputs && inputs.every((s, i) => s === lastInputs[i])) {
    return { cards: lastCards, aggregate: lastAggregate };
  }
  lastInputs = inputs;

  const cards = [];
  for (let i = 0; i < KINDS.length; i++) {
    cardsForSlice(KINDS[i].kind, inputs[i]).forEach((c) => cards.push(c));
  }
  // LIFO — newest startedAt first.
  cards.sort((a, b) => (b.startedAt || 0) - (a.startedAt || 0));

  const aggregate = cards.reduce(
    (acc, c) => {
      if (c.state === "running") {
        acc.running += 1;
        acc.runningPercent += c.percent;
      } else if (c.state === "done") acc.done += 1;
      else if (c.state === "warning") acc.warnings += 1;
      else if (c.state === "error") acc.errors += 1;
      acc.total += 1;
      return acc;
    },
    { running: 0, done: 0, warnings: 0, errors: 0, total: 0, runningPercent: 0 },
  );
  // The bubble's ring means "how far along is the running work" — terminal
  // cards sit at 100 and would permanently inflate it (warnings/errors
  // deliberately linger until acknowledged).
  aggregate.percent =
    aggregate.running > 0
      ? aggregate.runningPercent / aggregate.running
      : aggregate.total > 0
        ? 100
        : 0;
  delete aggregate.runningPercent;
  lastCards = cards;
  lastAggregate = aggregate;
  return { cards, aggregate };
}

export function selectProgressCards(state) {
  return compute(state).cards;
}

export function selectProgressAggregate(state) {
  return compute(state).aggregate;
}

export function _resetSelectorCacheForTests() {
  lastInputs = null;
  lastCards = [];
  lastAggregate = { running: 0, done: 0, warnings: 0, errors: 0, total: 0, percent: 0 };
}
